import moment from "moment";

const ROLE_CLAIM =
  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

export function hasPayrollAccess(user) {
  const roles = user?.[ROLE_CLAIM];
  if (!roles) return false;
  return roles.includes("System_Admin") || roles.includes("payroll_User");
}

function paylistPaymentDate(row) {
  if (!row) return 0;
  return (
    row.rpe_fecha_pago ||
    row.rpc_fecha_pago ||
    row.rpe_Fecha_pago ||
    row.fecha_pago ||
    0
  );
}

export function latestPaylistRow(paylist) {
  if (!Array.isArray(paylist) || paylist.length === 0) return null;
  return [...paylist].sort(
    (a, b) => new Date(paylistPaymentDate(b)) - new Date(paylistPaymentDate(a))
  )[0];
}

/** Job title from salarios row (rpe_plaza / rpc_plaza). */
function pickPlazaFromRow(row) {
  if (!row || typeof row !== "object") return "";
  const direct = [
    row.rpe_plaza,
    row.rpe_Plaza,
    row.rpc_plaza,
    row.rpc_Plaza,
    row.plaza,
  ];
  for (const v of direct) {
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  for (const key of Object.keys(row)) {
    if (!/plaza/i.test(key)) continue;
    const v = row[key];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

export function findPlazaInPaylist(paylist) {
  if (!Array.isArray(paylist)) return "";
  for (const row of paylist) {
    const p = pickPlazaFromRow(row);
    if (p) return p;
  }
  return "";
}

function toNumber(value) {
  if (value == null || value === "") return 0;
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/** Costa Rica cédula: 9 digits (not internal plaza codes). */
function looksLikeCedula(value) {
  const digits = String(value).replace(/\D/g, "");
  return digits.length >= 9 && digits.length <= 12;
}

function pickCedulaFromRow(row) {
  if (!row || typeof row !== "object") return "";
  const direct = [
    row.rpe_cedula,
    row.rpe_Cedula,
    row.rpc_cedula,
    row.rpc_Cedula,
    row.cedula,
    row.Cedula,
    row.numero_cedula,
    row.rpe_numero_cedula,
    row.rpe_identificacion,
    row.identificacion,
  ];
  for (const v of direct) {
    if (v != null && looksLikeCedula(v)) {
      return String(v).replace(/\D/g, "");
    }
  }
  for (const key of Object.keys(row)) {
    if (!/cedula|identific|documento/i.test(key)) continue;
    const v = row[key];
    if (v != null && looksLikeCedula(v)) {
      return String(v).replace(/\D/g, "");
    }
  }
  return "";
}

/** Search all payslips for a valid cédula field (rpe_codplz is NOT cédula). */
export function findCedulaInPaylist(paylist) {
  if (!Array.isArray(paylist)) return "";
  for (const row of paylist) {
    const c = pickCedulaFromRow(row);
    if (c) return c;
  }
  return "";
}

const CCSS_DEDUCTION_CODE = 6005;

/** Recibos especiales donde bruto === net_neto (aguinaldo, etc.) — no entran al agregado mensual. */
export function isSpecialEqualBrutoNetRow(row) {
  const bruto = toNumber(row?.bruto);
  const net = toNumber(row?.net_neto ?? row?.rpe_salario);
  return bruto > 0 && Math.abs(bruto - net) < 1;
}

/** Planilla Semanal salarial; excluye aguinaldo e ingresos no salariales. */
export function isWeeklyPayrollRow(row) {
  if (!row || isSpecialEqualBrutoNetRow(row)) return false;
  const desc = String(row.rpe_tpl_descripcion || "").toLowerCase();
  const cod = String(row.rpe_codpla || "").toUpperCase();
  if (/aguinaldo|no salarial/.test(desc)) return false;
  if (cod.startsWith("NS")) return false;
  return /planilla\s+semanal/i.test(desc) || /^CR\d/.test(cod);
}

function payPeriodOverlapsMonth(row, monthStart, monthEnd) {
  const ini = moment(row.rpe_fecha_ini);
  const fin = moment(row.rpe_fecha_fin);
  if (!ini.isValid() || !fin.isValid()) return false;
  return ini.isSameOrBefore(monthEnd) && fin.isSameOrAfter(monthStart);
}

/**
 * Rows whose pay period overlaps the target calendar month.
 * @param {Date|string|moment.Moment} [referenceDate] — document date (for tests); default now
 */
export function rowsForPreviousMonth(paylist, referenceDate = null) {
  if (!Array.isArray(paylist) || paylist.length === 0) return { rows: [], target: null };

  const ref = referenceDate ? moment(referenceDate) : moment();
  const target = ref.clone().subtract(1, "month");
  const monthStart = target.clone().startOf("month");
  const monthEnd = target.clone().endOf("month");

  let rows = paylist.filter((r) =>
    payPeriodOverlapsMonth(r, monthStart, monthEnd)
  );

  if (rows.length === 0) {
    const latest = latestPaylistRow(paylist);
    if (!latest) return { rows: [], target };
    const m = moment(paylistPaymentDate(latest));
    if (!m.isValid()) return { rows: [], target };
    const fbStart = m.clone().startOf("month");
    const fbEnd = m.clone().endOf("month");
    rows = paylist.filter((r) => payPeriodOverlapsMonth(r, fbStart, fbEnd));
    return { rows, target: m.clone().subtract(1, "month") };
  }

  return { rows, target };
}

export function shouldUseMonthlySalaryRubro(rows) {
  const semanal = rows.filter(isWeeklyPayrollRow);
  if (semanal.length === 0) return false;
  const salario = toNumber(semanal[0].rpe_salario);
  if (salario <= 0) return false;
  const sumBruto = semanal.reduce((s, r) => s + toNumber(r.bruto), 0);
  return Math.abs(sumBruto - salario) > 1;
}

function sumCcssDeductions(row) {
  const list = row.rpe_descuentos;
  if (!Array.isArray(list)) return 0;
  return list.reduce((s, d) => {
    if (Number(d.rpe_codigo_deduccion) !== CCSS_DEDUCTION_CODE) return s;
    return s + toNumber(d.rpe_deduccion);
  }, 0);
}

/**
 * Neto de constancia: aplica tasa CCSS del mes sobre rpe_salario (sin deducciones voluntarias).
 */
export function computeCertificateNet(semanalRows, gross) {
  const grossNum = toNumber(gross);
  if (grossNum <= 0 || !Array.isArray(semanalRows) || semanalRows.length === 0) {
    return 0;
  }
  const totalBruto = semanalRows.reduce((s, r) => s + toNumber(r.bruto), 0);
  const totalCcss = semanalRows.reduce((s, r) => s + sumCcssDeductions(r), 0);
  if (totalBruto <= 0) return grossNum;
  const net = grossNum * (1 - totalCcss / totalBruto);
  return Math.round(net * 100) / 100;
}

function latestWeeklyRowInSet(rows) {
  const semanal = rows.filter(isWeeklyPayrollRow);
  if (semanal.length === 0) return null;
  return [...semanal].sort(
    (a, b) => new Date(paylistPaymentDate(b)) - new Date(paylistPaymentDate(a))
  )[0];
}

function aggregateLegacySum(rows, target) {
  const gross = rows.reduce((s, r) => s + toNumber(r.bruto), 0);
  const net = rows.reduce(
    (s, r) => s + toNumber(r.net_neto ?? r.rpe_salario),
    0
  );
  const monthLabel =
    rows.length > 0
      ? moment(paylistPaymentDate(rows[0])).format("MMMM")
      : target.format("MMMM");
  return { gross, net, monthLabel, rows, source: "legacy_sum" };
}

/**
 * Bruto/neto del mes anterior (último mes). Usa rubro rpe_salario cuando la suma semanal no coincide.
 * @param {Date|string} [referenceDate] — fecha del certificado (opcional, para pruebas)
 */
export function aggregatePreviousMonthPaylist(paylist, referenceDate = null) {
  if (!Array.isArray(paylist) || paylist.length === 0) {
    return { gross: 0, net: 0, monthLabel: "", rows: [], source: "empty" };
  }

  const { rows, target } = rowsForPreviousMonth(paylist, referenceDate);
  const targetMoment = target || moment(referenceDate || undefined).subtract(1, "month");

  if (rows.length === 0) {
    return { gross: 0, net: 0, monthLabel: targetMoment.format("MMMM"), rows: [], source: "empty" };
  }

  const monthLabel = targetMoment.format("MMMM");

  if (shouldUseMonthlySalaryRubro(rows)) {
    const semanal = rows.filter(isWeeklyPayrollRow);
    const latestWeekly = latestWeeklyRowInSet(rows);
    const gross = toNumber(latestWeekly?.rpe_salario);
    const net = computeCertificateNet(semanal, gross);
    return { gross, net, monthLabel, rows: semanal, source: "rpe_salario" };
  }

  return aggregateLegacySum(rows, targetMoment);
}

function deepFindValue(obj, keyPattern) {
  if (obj == null) return undefined;
  if (typeof obj !== "object") return undefined;
  if (Array.isArray(obj)) {
    for (const item of obj) {
      const v = deepFindValue(item, keyPattern);
      if (v != null) return v;
    }
    return undefined;
  }
  for (const [k, v] of Object.entries(obj)) {
    if (keyPattern.test(k) && v != null && v !== "") return v;
    if (typeof v === "object") {
      const nested = deepFindValue(v, keyPattern);
      if (nested != null) return nested;
    }
  }
  return undefined;
}

/**
 * Normalizes pay frequency from salarios API (rpe_tpl_descripcion) for certificate text.
 * e.g. "Planilla Quincenal" → "quincenal" (as in official email).
 */
export function normalizePayFrequency(raw) {
  if (raw == null || raw === "") return "quincenal";
  const s = String(raw).trim().toLowerCase();
  if (/quincenal/.test(s)) return "quincenal";
  if (/semanal/.test(s)) return "semanal";
  if (/mensual/.test(s)) return "mensual";
  if (/bimestral/.test(s)) return "bimestral";
  return s.replace(/^planilla\s+/i, "").trim() || "quincenal";
}

function pickPayFrequencyFromRow(row) {
  if (!row || typeof row !== "object") return "";
  const candidates = [
    row.rpe_periodicidad_pago,
    row.rpe_periodicidad,
    row.periodicidad_pago,
    row.periodicidad,
    row.rpe_tpl_descripcion,
    row.rpe_frecuencia_pago,
  ];
  for (const v of candidates) {
    if (v != null && String(v).trim() !== "") {
      return normalizePayFrequency(v);
    }
  }
  for (const key of Object.keys(row)) {
    if (!/periodicidad|frecuencia|tpl_descripcion|planilla/i.test(key)) continue;
    const v = row[key];
    if (v != null && String(v).trim() !== "") {
      return normalizePayFrequency(v);
    }
  }
  return "";
}

/** Parse certificados API JSON (same source as the email certificate). */
export function parseCertificateApiPayload(json) {
  if (!json || typeof json !== "object") return null;
  const cedulaRaw =
    deepFindValue(json, /cedula/i) ??
    json.cedula ??
    json.Cedula ??
    json.numero_cedula;
  const cedula =
    cedulaRaw != null && looksLikeCedula(cedulaRaw)
      ? String(cedulaRaw).replace(/\D/g, "")
      : "";

  return {
    fullName:
      deepFindValue(json, /nombre|empleado|name/i) ??
      json.nombre_empleado ??
      json.nombre ??
      "",
    cedula,
    position: deepFindValue(json, /puesto|plaza|cargo|position/i) ?? "",
    hireDate: deepFindValue(json, /fecha.*ingreso|ingreso/i) ?? null,
    hourlySalary: toNumber(
      deepFindValue(json, /salario.*hora|hora/i) ?? json.salario_hora
    ),
    gross: toNumber(deepFindValue(json, /bruto/i) ?? json.bruto),
    net: toNumber(
      deepFindValue(json, /neto/i) ?? json.neto ?? json.net_neto
    ),
    payFrequency: normalizePayFrequency(
      deepFindValue(json, /periodicidad|frecuencia|planilla/i) ?? ""
    ),
    monthLabel: deepFindValue(json, /mes/i) ?? "",
  };
}

/**
 * Merges certificados API data, payroll salarios (monthly totals), and user.
 */
function rosterCedula(rosterProfile) {
  const raw = rosterProfile?.cedula ?? rosterProfile?.Cedula;
  if (raw == null || raw === "") return "";
  return looksLikeCedula(raw) ? String(raw).replace(/\D/g, "") : String(raw).trim();
}

export function buildCertificateEmployeeData(
  user,
  paylist,
  employeeId,
  apiData = null,
  rosterProfile = null
) {
  const latest = latestPaylistRow(paylist) || {};
  const monthly = aggregatePreviousMonthPaylist(paylist);
  const hireRaw =
    apiData?.hireDate ||
    latest.rpe_Fecha_ingreso ||
    latest.rpe_fecha_ingreso;

  const gross =
    apiData?.gross > 0 ? apiData.gross : monthly.gross;
  const net = apiData?.net > 0 ? apiData.net : monthly.net;

  const cedula =
    (apiData?.cedula && looksLikeCedula(apiData.cedula)
      ? String(apiData.cedula).replace(/\D/g, "")
      : "") ||
    rosterCedula(rosterProfile) ||
    findCedulaInPaylist(paylist) ||
    "";

  return {
    employeeId: employeeId || user?.badge || "",
    fullName:
      apiData?.fullName ||
      rosterProfile?.fullName ||
      latest.rpe_nombre_empleado ||
      user?.fullname ||
      user?.displayName ||
      "",
    position:
      apiData?.position ||
      pickPlazaFromRow(latest) ||
      findPlazaInPaylist(paylist) ||
      rosterProfile?.position ||
      "",
    cedula,
    hireDate: hireRaw ? moment(hireRaw).toDate() : null,
    hourlySalary:
      apiData?.hourlySalary > 0
        ? apiData.hourlySalary
        : latest.rpe_salario_hora,
    gross,
    net,
    payFrequency:
      (apiData?.payFrequency &&
        normalizePayFrequency(apiData.payFrequency)) ||
      pickPayFrequencyFromRow(latest) ||
      "quincenal",
    monthLabel: apiData?.monthLabel || monthly.monthLabel,
    company: latest.rpe_nombre_empresa || "Foundever Costa Rica",
  };
}

export function formatColones(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "¢ 0.00";
  return new Intl.NumberFormat("es-CR", {
    style: "currency",
    currency: "CRC",
  }).format(n);
}

export function certificateFilename(type) {
  const ts = moment().format("YYYYMMDD_HHmmss");
  if (type === "1") {
    return `Foundever - Certificado de trabajo ${ts}.pdf`;
  }
  return `Foundever - Certificado Salarial ${ts}.pdf`;
}
